import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '@/utils/axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { CheckCircle, XCircle, UserPlus, Users, ArrowLeft, Shield, Calendar, Mail, Trash2, Pencil, Search, Clock, Copy, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const ROLES = [
  { value: 'administrador', label: 'Administrador', color: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'medico', label: 'Médico', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'dentista', label: 'Dentista', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  { value: 'enfermeiro', label: 'Enfermeiro(a)', color: 'bg-green-100 text-green-700 border-green-200' },
  { value: 'farmaceutico', label: 'Farmacêutico(a)', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { value: 'recepcao', label: 'Recepção', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 'secretaria', label: 'Secretária', color: 'bg-pink-100 text-pink-700 border-pink-200' },
  { value: 'auditor', label: 'Auditor', color: 'bg-gray-100 text-gray-700 border-gray-200' },
];

const getRoleBadge = (role) => {
  const r = ROLES.find(x => x.value === role);
  if (!r) return <Badge variant="outline">{role}</Badge>;
  return <Badge className={`${r.color} border font-medium text-xs`}>{r.label}</Badge>;
};

export default function ApproveUsers() {
  const navigate = useNavigate();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [inviteResult, setInviteResult] = useState(null);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    if (currentUser.role !== 'administrador') {
      toast.error('Apenas administradores podem gerenciar usuários');
      navigate('/');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pendingRes, approvedRes] = await Promise.all([
        axiosInstance.get('/admin/pending-users'),
        axiosInstance.get('/admin/approved-users'),
      ]);
      setPendingUsers(pendingRes.data.pending_users || []);
      setApprovedUsers(approvedRes.data.users || []);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId, approve) => {
    try {
      await axiosInstance.post('/admin/approve-user', { user_id: userId, approved: approve });
      toast.success(approve ? 'Usuário aprovado!' : 'Usuário rejeitado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar');
    }
  };

  const handleDelete = async (userId, userName) => {
    try {
      await axiosInstance.delete(`/admin/users/${userId}`);
      toast.success(`"${userName}" removido com sucesso`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover usuário');
    }
  };

  const filteredUsers = approvedUsers.filter(u =>
    !searchTerm ||
    u.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.role?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="user-management-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Gestão de Usuários</h1>
              <p className="text-xs text-gray-500">{currentUser.tenant_nome || 'Minha Clínica'}</p>
            </div>
          </div>
          <Button onClick={() => setShowInviteModal(true)} className="gap-2" data-testid="invite-user-button">
            <UserPlus className="h-4 w-4" />
            Convidar Usuário
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{approvedUsers.length}</p>
                <p className="text-xs text-gray-500">Usuários ativos</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{pendingUsers.length}</p>
                <p className="text-xs text-gray-500">Pendentes</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-red-50 flex items-center justify-center">
                <Shield className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {approvedUsers.filter(u => u.role === 'administrador').length}
                </p>
                <p className="text-xs text-gray-500">Administradores</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="active" className="gap-2" data-testid="tab-active">
              <Users className="h-4 w-4" /> Ativos ({approvedUsers.length})
            </TabsTrigger>
            <TabsTrigger value="pending" className="gap-2 relative" data-testid="tab-pending">
              <Clock className="h-4 w-4" /> Pendentes
              {pendingUsers.length > 0 && (
                <span className="ml-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                  {pendingUsers.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Active Users Tab */}
          <TabsContent value="active" className="mt-0">
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por nome, email ou perfil..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-users"
                />
              </div>
            </div>

            {loading ? (
              <p className="text-center py-8 text-gray-500">Carregando...</p>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>{searchTerm ? 'Nenhum usuário encontrado' : 'Nenhum usuário ativo'}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredUsers.map(user => (
                  <Card key={user.id} className="border shadow-sm hover:shadow-md transition-shadow" data-testid={`user-card-${user.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-600 shrink-0">
                            {user.nome?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm text-gray-900 truncate">
                              {user.nome}
                              {user.id === currentUser.id && <span className="text-xs text-gray-400 ml-2">(você)</span>}
                            </p>
                            <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                              <Mail className="h-3 w-3" /> {user.email}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {getRoleBadge(user.role)}
                          {user.crm && (
                            <Badge variant="outline" className="text-xs">CRM {user.crm}</Badge>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => { setEditingUser(user); setShowEditModal(true); }}
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5 text-gray-500" />
                          </Button>
                          {user.id !== currentUser.id && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`delete-user-${user.id}`}>
                                  <Trash2 className="h-3.5 w-3.5 text-red-400" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Remover usuário</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Tem certeza que deseja remover <strong>{user.nome}</strong>? Esta ação não pode ser desfeita.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(user.id, user.nome)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Remover
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Pending Users Tab */}
          <TabsContent value="pending" className="mt-0">
            {loading ? (
              <p className="text-center py-8 text-gray-500">Carregando...</p>
            ) : pendingUsers.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>Nenhum usuário pendente de aprovação</p>
              </div>
            ) : (
              <div className="space-y-2">
                {pendingUsers.map(user => (
                  <Card key={user.id} className="border border-amber-200 bg-amber-50/50 shadow-sm" data-testid={`pending-card-${user.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center text-sm font-bold text-amber-700 shrink-0">
                            {user.nome?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm text-gray-900 truncate">{user.nome}</p>
                            <p className="text-xs text-gray-500 truncate">{user.email}</p>
                            <div className="flex items-center gap-2 mt-1">
                              {getRoleBadge(user.role)}
                              <span className="text-[10px] text-gray-400 flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {user.data_cadastro ? new Date(user.data_cadastro).toLocaleDateString('pt-BR') : ''}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            size="sm"
                            onClick={() => handleApprove(user.id, true)}
                            className="gap-1 bg-green-600 hover:bg-green-700"
                            data-testid={`approve-${user.id}`}
                          >
                            <CheckCircle className="h-4 w-4" /> Aprovar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleApprove(user.id, false)}
                            className="gap-1 text-red-600 border-red-200 hover:bg-red-50"
                            data-testid={`reject-${user.id}`}
                          >
                            <XCircle className="h-4 w-4" /> Rejeitar
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Invite Modal */}
      <InviteUserModal
        open={showInviteModal}
        onClose={() => { setShowInviteModal(false); setInviteResult(null); }}
        onSuccess={(result) => { setInviteResult(result); fetchData(); }}
        inviteResult={inviteResult}
      />

      {/* Edit Modal */}
      <EditUserModal
        open={showEditModal}
        onClose={() => { setShowEditModal(false); setEditingUser(null); }}
        onSuccess={() => { setShowEditModal(false); setEditingUser(null); fetchData(); }}
        user={editingUser}
      />
    </div>
  );
}

function InviteUserModal({ open, onClose, onSuccess, inviteResult }) {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('medico');
  const [crm, setCrm] = useState('');
  const [senha, setSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nome.trim() || !email.trim()) {
      toast.error('Nome e email são obrigatórios');
      return;
    }
    setLoading(true);
    try {
      const payload = { nome: nome.trim(), email: email.trim(), role, crm: crm.trim() };
      if (senha.trim()) payload.senha = senha.trim();
      const res = await axiosInstance.post('/admin/invite-user', payload);
      toast.success(res.data.message);
      onSuccess(res.data);
      setNome(''); setEmail(''); setRole('medico'); setCrm(''); setSenha('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao convidar usuário');
    } finally {
      setLoading(false);
    }
  };

  const copyPassword = (pwd) => {
    navigator.clipboard.writeText(pwd);
    toast.success('Senha copiada!');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="invite-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" /> Convidar Usuário
          </DialogTitle>
          <DialogDescription>
            O novo usuário será criado já aprovado e vinculado à sua clínica.
          </DialogDescription>
        </DialogHeader>

        {inviteResult ? (
          <div className="space-y-4 py-2">
            <div className="rounded-lg bg-green-50 border border-green-200 p-4 text-center">
              <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-2" />
              <p className="font-semibold text-green-800">Usuário criado com sucesso!</p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-gray-500">Senha temporária</Label>
              <div className="flex items-center gap-2">
                <Input value={inviteResult.senha_temporaria} readOnly className="font-mono" data-testid="temp-password" />
                <Button size="icon" variant="outline" onClick={() => copyPassword(inviteResult.senha_temporaria)} data-testid="copy-password">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-[11px] text-gray-400">Compartilhe esta senha com o usuário. Ele poderá alterá-la depois.</p>
            </div>
            <Button className="w-full" onClick={onClose}>Fechar</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Nome completo *</Label>
              <Input value={nome} onChange={e => setNome(e.target.value)} placeholder="Dr. João Silva" data-testid="invite-nome" />
            </div>
            <div className="space-y-1.5">
              <Label>Email *</Label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="joao@clinica.com" data-testid="invite-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Perfil</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(role === 'medico' || role === 'dentista') && (
              <div className="space-y-1.5">
                <Label>CRM/CRO</Label>
                <Input value={crm} onChange={e => setCrm(e.target.value)} placeholder="12345" data-testid="invite-crm" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Senha (opcional)</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={senha}
                  onChange={e => setSenha(e.target.value)}
                  placeholder="Deixe vazio para senha padrão"
                  data-testid="invite-senha"
                />
                <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-[11px] text-gray-400">Se vazio, a senha padrão será "mudar123"</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
              <Button type="submit" disabled={loading} className="gap-2" data-testid="invite-submit">
                {loading ? 'Criando...' : <><UserPlus className="h-4 w-4" /> Criar Usuário</>}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditUserModal({ open, onClose, onSuccess, user }) {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [crm, setCrm] = useState('');
  const [ativo, setAtivo] = useState(true);
  const [novaSenha, setNovaSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setNome(user.nome || '');
      setEmail(user.email || '');
      setRole(user.role || 'medico');
      setCrm(user.crm || '');
      setAtivo(user.ativo !== false);
      setNovaSenha('');
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nome.trim() || !email.trim()) {
      toast.error('Nome e email são obrigatórios');
      return;
    }
    setLoading(true);
    try {
      const data = { nome: nome.trim(), email: email.trim(), role, crm: crm.trim(), ativo };
      if (novaSenha.trim()) data.nova_senha = novaSenha.trim();
      await axiosInstance.put(`/admin/users/${user.id}`, data);
      toast.success('Usuário atualizado!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="edit-user-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5" /> Editar Usuário
          </DialogTitle>
          <DialogDescription>Editar informações de {user.nome}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Nome</Label>
            <Input value={nome} onChange={e => setNome(e.target.value)} data-testid="edit-nome" />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="edit-email" />
          </div>
          <div className="space-y-1.5">
            <Label>Perfil</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="edit-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {(role === 'medico' || role === 'dentista') && (
            <div className="space-y-1.5">
              <Label>CRM/CRO</Label>
              <Input value={crm} onChange={e => setCrm(e.target.value)} data-testid="edit-crm" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Nova senha (opcional)</Label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={novaSenha}
                onChange={e => setNovaSenha(e.target.value)}
                placeholder="Deixe vazio para não alterar"
                data-testid="edit-senha"
              />
              <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between py-1">
            <Label>Conta ativa</Label>
            <button
              type="button"
              onClick={() => setAtivo(!ativo)}
              className={`relative w-10 h-6 rounded-full transition-colors ${ativo ? 'bg-green-500' : 'bg-gray-300'}`}
              data-testid="edit-ativo-toggle"
            >
              <span className={`absolute top-1 h-4 w-4 rounded-full bg-white transition-transform ${ativo ? 'left-5' : 'left-1'}`} />
            </button>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={loading} data-testid="edit-submit">
              {loading ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
